import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Mic, Play, RotateCcw, Square, Sparkles, Volume2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { encodeOwnVoice } from '../lib/audioDisguise';

type Recognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  start: () => void;
  stop: () => void;
};
type RecognitionCtor = new () => Recognition;
declare global { interface Window { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor; } }

type Props = {
  questionText: string;
  questionAudioUrl?: string;
  testId: string;
  questionId: string;
  light: boolean;
  onSaved: (audioUrl: string, transcript: string) => void;
};
type State = 'idle' | 'consent' | 'greeting' | 'listening' | 'review' | 'saving';

export default function LiveYhuuAi({ questionText, questionAudioUrl, testId, questionId, light, onSaved }: Props) {
  const [state, setState] = useState<State>('idle');
  const [transcript, setTranscript] = useState('');
  const [draftBlob, setDraftBlob] = useState<Blob | null>(null);
  const [draftUrl, setDraftUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recognitionRef = useRef<Recognition | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const transcriptRef = useRef('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Speech recognition is optional: some Safari/iPhone browsers can record
  // audio but cannot provide a transcript.
  const supported = typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!window.speechSynthesis;
  const colors = light ? { card: '#fff8fb', border: '#fbcfe0', text: '#18181b', muted: '#9d6b7a' } : { card: '#231821', border: '#4b2834', text: '#f4f4f5', muted: '#c4a2ad' };

  useEffect(() => () => {
    recognitionRef.current?.stop();
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(track => track.stop());
    window.speechSynthesis?.cancel();
    audioRef.current?.pause();
    if (draftUrl) URL.revokeObjectURL(draftUrl);
  }, [draftUrl]);

  const reset = () => {
    recognitionRef.current?.stop();
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(track => track.stop());
    window.speechSynthesis?.cancel();
    audioRef.current?.pause();
    if (draftUrl) URL.revokeObjectURL(draftUrl);
    recorderRef.current = null;
    recognitionRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
    transcriptRef.current = '';
    setTranscript(''); setDraftBlob(null); setDraftUrl(null); setError(''); setState('idle');
  };

  const finish = () => {
    recognitionRef.current?.stop();
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    streamRef.current?.getTracks().forEach(track => track.stop());
  };

  const listen = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        try {
          const rawBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const wavBlob = await encodeOwnVoice(rawBlob);
          setDraftBlob(wavBlob); setDraftUrl(URL.createObjectURL(wavBlob)); setState('review');
        } catch (e) {
          console.error('Live Yhuu AI audio processing error', e);
          setError('The audio could not be prepared. Please use the normal voice answer option.');
          setState('consent');
        }
      };
      const RecognitionApi = window.SpeechRecognition || window.webkitSpeechRecognition;
      recorderRef.current = recorder;
      recorder.start();
      setState('listening');
      // A transcription failure must not cancel the microphone recording.
      if (RecognitionApi) {
        const recognition = new RecognitionApi();
        recognition.continuous = true; recognition.interimResults = true; recognition.lang = navigator.language || 'en-US';
        recognition.onresult = event => {
          let text = '';
          for (let i = 0; i < event.results.length; i++) text += event.results[i][0]?.transcript || '';
          transcriptRef.current = text.trim(); setTranscript(transcriptRef.current);
        };
        recognition.onerror = e => { if (e.error !== 'no-speech' && e.error !== 'aborted') setError('Transcript unavailable, but your audio is still being recorded.'); };
        recognitionRef.current = recognition;
        try { recognition.start(); } catch { setError('Transcript unavailable, but your audio is still being recorded.'); }
      } else {
        setError('Transcript unavailable, but your audio is still being recorded.');
      }
    } catch (e) {
      console.error('Live Yhuu AI microphone error', e);
      setError('Microphone access was not available. You can use the normal answer options instead.');
      setState('consent');
    }
  };

  const start = () => {
    setError(''); setState('greeting');
    const greet = new SpeechSynthesisUtterance('Hi, welcome to Yhuu. I am Yhuu AI, here to deliver a question. I will listen to your answer and save it for the sender.');
    greet.rate = 0.96;
    greet.onend = () => {
      if (questionAudioUrl) {
        const audio = new Audio(questionAudioUrl); audioRef.current = audio;
        audio.onended = () => { void listen(); };
        audio.onerror = () => { window.speechSynthesis.speak(new SpeechSynthesisUtterance(questionText)); setTimeout(() => void listen(), 1800); };
        void audio.play().catch(() => { window.speechSynthesis.speak(new SpeechSynthesisUtterance(questionText)); setTimeout(() => void listen(), 1800); });
      } else {
        const question = new SpeechSynthesisUtterance(`Here is the question: ${questionText}`);
        question.rate = 0.96; question.onend = () => { void listen(); }; window.speechSynthesis.speak(question);
      }
    };
    window.speechSynthesis.cancel(); window.speechSynthesis.speak(greet);
  };

  const save = async () => {
    if (!draftBlob) return;
    setState('saving'); setError('');
    try {
      const path = `answers/${testId}-${questionId}/live-ai-${Date.now()}.wav`;
      const { error: uploadError } = await supabase.storage.from('voice-notes').upload(path, draftBlob, { contentType: 'audio/wav' });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('voice-notes').getPublicUrl(path);
      onSaved(data.publicUrl, transcript.trim() || 'Live Yhuu AI voice answer recorded.');
      reset();
    } catch (e) {
      console.error('Live Yhuu AI save error', e); setError('The recording could not be saved. Please try again.'); setState('review');
    }
  };

  if (!supported) return null;
  return (
    <div className="rounded-2xl border p-4 space-y-3" style={{ background: colors.card, borderColor: colors.border }}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-xl bg-purple-500/15 flex items-center justify-center shrink-0"><Sparkles size={17} color="#a855f7" /></span>
        <div><p className="text-xs font-black uppercase tracking-widest" style={{ color: '#a855f7' }}>Live Yhuu AI</p><p className="text-xs mt-1 leading-relaxed" style={{ color: colors.muted }}>Yhuu AI will greet you, play or read the question, listen to your answer, and save it for the sender.</p></div>
      </div>
      {state === 'idle' && <button type="button" onClick={() => setState('consent')} className="w-full rounded-xl py-3 flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest text-white" style={{ background: '#a855f7' }}><Play size={15} /> Start Live Yhuu AI</button>}
      {state === 'consent' && <div className="space-y-3"><p className="text-xs leading-relaxed" style={{ color: colors.text }}>Yhuu AI will use your microphone to record your answer and share the saved answer with the sender. You can choose the normal text or voice option instead.</p><div className="flex gap-2"><button type="button" onClick={start} className="flex-1 rounded-xl py-3 text-xs font-black uppercase tracking-widest text-white" style={{ background: '#a855f7' }}>I’m ready</button><button type="button" onClick={reset} className="px-4 rounded-xl border text-xs font-bold" style={{ color: colors.text, borderColor: colors.border }}>Cancel</button></div></div>}
      {state === 'greeting' && <p className="flex items-center justify-center gap-2 text-sm font-bold" style={{ color: colors.text }}><Volume2 size={16} className="animate-pulse" /> Yhuu AI is greeting you and asking the question…</p>}
      {state === 'listening' && <div className="space-y-3"><p className="flex items-center justify-center gap-2 text-sm font-bold text-purple-600"><Mic size={16} className="animate-pulse" /> Listening to your answer…</p><p className="min-h-10 rounded-xl border p-3 text-sm" style={{ borderColor: colors.border, color: colors.text }}>{transcript || 'Speak when you are ready.'}</p><button type="button" onClick={finish} className="w-full rounded-xl py-3 flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest bg-zinc-900 text-white"><Square size={13} /> Finish answer</button></div>}
      {state === 'review' && <div className="space-y-3"><p className="text-xs font-black uppercase tracking-widest" style={{ color: colors.muted }}>Review before sending</p>{draftUrl && <audio controls src={draftUrl} className="w-full" />}<p className="rounded-xl border p-3 text-sm" style={{ borderColor: colors.border, color: colors.text }}>{transcript || 'Audio answer ready; no transcript was available.'}</p>{error && <p className="text-xs text-rose-600">{error}</p>}<div className="flex gap-2"><button type="button" onClick={save} className="flex-1 rounded-xl py-3 flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest text-white" style={{ background: '#a855f7' }}><Check size={15} /> Save answer</button><button type="button" onClick={reset} className="px-4 rounded-xl border" style={{ color: colors.text, borderColor: colors.border }}><RotateCcw size={16} /></button></div></div>}
      {state === 'saving' && <p className="flex items-center justify-center gap-2 text-sm font-bold" style={{ color: colors.text }}><Loader2 size={16} className="animate-spin" /> Saving your answer…</p>}
      {error && state === 'consent' && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}
